/*
 * Industry content — `es` (international market, Spanish mirror of en).
 *
 * This is the Spanish (es) international industry set, a translation of the
 * international variant of every industry (the resolved `*Intl` copy): generic
 * time-registration framing, no belgium-specific pains/features, and
 * `compliance.slug` / `relatedArticles` pointing only at articles that exist
 * under `/en/` (the 4 market-agnostic posts). `nl.ts` carries the belgium
 * variant. Pure data (NFR10).
 */
import type { Industry } from "./types";

export const INDUSTRIES_ES: Industry[] = [
  {
    slug: "construction",
    name: "Construcción",
    headline: "Control horario para equipos de construcción: fichaje verificado por ubicación, horas por proyecto y control de presupuesto, en una sola app",
    intro: "Cuadrillas repartidas entre obras, horas anotadas en papel, horas de subcontratistas imposibles de cuadrar. Done-it le da a cada obra un parte horario digital a prueba de fallos que alimenta tu nómina con un solo clic.",
    pains: [
      {
        title: "Sin un registro fiable de quién trabajó dónde",
        body: "Con varias obras activas y cuadrillas rotativas, los partes horarios manuales fallan por diseño. Los trabajadores redondean al alza, olvidan registros o los rellenan días después, dejándote cifras de nómina que no puedes defender.",
      },
      {
        title: "Los sobrecostes salen a la luz demasiado tarde",
        body: "Cuando te das cuenta de que una obra ha consumido sus horas previstas, el margen ya se ha esfumado. No hay una cuenta atrás en tiempo real, solo una sorpresa desagradable a la hora de facturar.",
      },
      {
        title: "Preparar la nómina se lleva horas cada mes",
        body: "Recopilar partes horarios, cuadrarlos con los proyectos y preparar la exportación de la nómina lleva días. Cada registro es un posible error.",
      },
    ],
    features: [
      {
        title: "Fichaje verificado por ubicación por obra",
        body: "Con un toque el trabajador ficha, verificado por ubicación, y vincula las horas al proyecto correcto. Sin lectores de tarjetas ni terminales: solo el smartphone que tu cuadrilla ya lleva encima.",
      },
      {
        title: "Horas restantes en tiempo real por proyecto",
        body: "Define las horas previstas como presupuesto al abrir el proyecto. Los trabajadores ven una cuenta atrás de lo que queda; tú la ves en directo en tu panel. Los sobrecostes se ven antes de que ocurran.",
      },
      {
        title: "Gestión y presencia de subcontratistas",
        body: "Añade subcontratistas a un proyecto, asígnalos al fichaje y controla sus horas junto a las de tu propia cuadrilla, en la misma vista.",
      },
      {
        title: "Exportación de nómina con un clic",
        body: "Todas las horas registradas —por trabajador, por proyecto— se exportan a una hoja de cálculo limpia para tu gestoría de nóminas. Sin preparación manual, sin errores.",
      },
    ],
    compliance: {
      label: "Registro horario digital",
      title: "Fichaje verificado por ubicación pensado para obras de construcción",
      body: "Done-it ofrece fichaje verificado por ubicación, control horario por proyecto y una exportación de nómina limpia para equipos de construcción. Para equipos que operan en obras belgas, también alimenta automáticamente el checkin@work.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "La cuadrilla llega a la obra", body: "Cada trabajador toca una vez para fichar. El GPS confirma la ubicación, marca la hora del registro y vincula las horas al proyecto. Sin papel, sin terminal." },
      { step: "Lo ves todo en directo", body: "Horas, presupuesto restante, presencia de subcontratistas y cualquier trabajo extra reportado desde la obra: todo en tu panel, en tiempo real." },
      { step: "Cierre de mes en minutos", body: "Exporta todas las horas por trabajador y por proyecto. Envíalas a tu gestoría de nóminas. Done-it genera informes limpios y listos para auditoría de forma automática." },
    ],
    stats: [
      { value: "1 toque", label: "para fichar en cualquier obra: sin terminal, sin papel" },
      { value: "1 clic", label: "exportación de nómina lista para tu gestoría" },
      { value: "En directo", label: "vista de horas restantes por proyecto y obra" },
    ],
    quote: {
      text: "Nuestra cuadrilla ficha por sí sola en la obra. A fin de mes, la nómina simplemente cuadra: dejamos de perseguir partes horarios por completo.",
      who: "Propietario, empresa de construcción · 14 empleados",
      result: "5 horas a la semana ahorradas en administración",
    },
    relatedSlugs: ["installation", "maintenance", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "cleaning",
    name: "Limpieza",
    headline: "Control horario para empresas de limpieza: fichaje verificado por ubicación, prueba de presencia y nómina en una sola app",
    intro: "Los limpiadores se mueven entre ubicaciones todo el día. Done-it controla quién está dónde en tiempo real: fichaje verificado por ubicación en cada centro, deducción automática de pausas y una exportación de nómina limpia a fin de mes.",
    pains: [
      {
        title: "Demostrar la presencia en cada ubicación",
        body: "Clientes e inspectores esperan registros de presencia a prueba de fallos. Las hojas manuales se pueden impugnar. Cada registro se verifica por ubicación.",
      },
      {
        title: "Equipos repartidos entre muchas ubicaciones",
        body: "Con limpiadores que se mueven entre centros a lo largo del día, no tienes una vista en directo de quién trabaja dónde, hasta que alguien llama.",
      },
      {
        title: "Errores de nómina por horas redondeadas",
        body: "Sin horas de entrada y salida precisas, la nómina se reconstruye de memoria. Las horas se redondean al alza, los costes se inflan y llegan las disputas.",
      },
    ],
    features: [
      {
        title: "Fichaje de entrada y salida en tiempo real",
        body: "Los trabajadores fichan entrada y salida en su móvil en cada ubicación, verificado por ubicación; el tiempo es exacto, con registro de pausas.",
        plan: "Time Basic",
      },
      {
        title: "Deducción automática de pausas",
        body: "Las pausas obligatorias se restan de la jornada laboral automáticamente: sin cálculos manuales, sin disputas.",
        plan: "Time Basic",
      },
      {
        title: "Vista en directo multiubicación",
        body: "Ve qué limpiadores están en qué ubicaciones ahora mismo. Detecta huecos antes que los clientes.",
      },
      {
        title: "Registro de ausencias con justificantes médicos digitales",
        body: "Los trabajadores suben certificados médicos y documentos de ausencia directamente desde la app. Crea los tipos de ausencia que tu equipo realmente usa.",
      },
      {
        title: "Exportación de nómina con un clic",
        body: "Las marcas de tiempo exactas pasan directamente a una exportación lista para la nómina de tu gestoría. Sin transcripción, sin errores.",
      },
    ],
    compliance: {
      label: "Presencia verificada por ubicación",
      title: "Presencia en tiempo real en cada ubicación de limpieza",
      body: "Done-it da a los equipos de limpieza fichaje verificado por ubicación en cada centro, deducción automática de pausas y una vista en directo de quién está en qué ubicación ahora mismo.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "El limpiador llega a la ubicación", body: "Un toque lo ficha. El GPS registra el centro y marca la hora del registro. El registro de pausas viene integrado en la app." },
      { step: "Ves cada ubicación en directo", body: "Quién está en qué centro ahora mismo: sin llamadas, sin conjeturas. Detecta huecos de cobertura antes que los clientes." },
      { step: "La nómina es un solo clic", body: "Todos los datos de presencia —horas exactas, ubicaciones y pausas— se exportan limpios para tu gestoría de nóminas." },
    ],
    stats: [
      { value: "1 toque", label: "para fichar en cualquier ubicación: sin terminal" },
      { value: "En directo", label: "vista de quién está en qué centro ahora mismo" },
      { value: "0", label: "registros duplicados: las horas pasan directas a la nómina" },
    ],
    quote: {
      text: "Por fin podemos demostrar quién limpió dónde y cuándo. Se acabaron las hojas de presencia y las disputas con clientes: el GPS se encarga.",
      who: "Responsable, empresa de limpieza · 9 empleados",
      result: "Cero redondeo al alza de horas",
    },
    relatedSlugs: ["maintenance", "field-service", "construction"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "installation",
    name: "Instalación",
    headline: "Control horario para equipos de instalación: horas por proyecto, navegación y control de presupuesto en una sola app",
    intro: "Envía a tus instaladores a la dirección correcta con todo lo que necesitan, vincula cada hora trabajada al trabajo correcto automáticamente y ve el presupuesto restante antes de que cierre la obra. Done-it es la forma más sencilla de mantener los proyectos de instalación a tiempo y en coste.",
    pains: [
      {
        title: "La información del trabajo no llega al campo a tiempo",
        body: "Las cuadrillas llegan a una obra sin la dirección, la persona de contacto o los detalles del alcance. Esa primera llamada son diez minutos de tiempo facturable perdidos antes de trabajar una sola hora.",
      },
      {
        title: "Las horas no se vinculan al proyecto correcto",
        body: "Cuando los trabajadores rellenan sus horas manualmente al final del día —si es que las rellenan—, las horas caen en el trabajo equivocado o desaparecen del todo. Las facturas no coinciden con la realidad.",
      },
      {
        title: "Sobrecostes detectados demasiado tarde",
        body: "Sin una cuenta atrás en tiempo real de las horas restantes por proyecto, descubres que un trabajo se ha pasado al cerrar la factura, no cuando aún puedes reaccionar.",
      },
      {
        title: "Difícil controlar las horas de subcontratistas",
        body: "Los subcontratistas que trabajan en la misma obra usan sistemas diferentes, rellenan las horas por separado o no las rellenan. Cuadrarlas a fin de mes es un proceso lento y propenso a errores.",
      },
    ],
    features: [
      {
        title: "Información del proyecto compartida directamente en la app",
        body: "Los trabajadores abren el proyecto en su móvil: dirección, horas previstas, persona de contacto y archivos subidos (planos, instrucciones, PDF). Todo lo que necesitan está ahí antes de llegar.",
        plan: "Project Basic",
      },
      {
        title: "Navegación a la obra con un botón",
        body: "Toca una vez y el móvil navega hasta la dirección del proyecto: integrado en la app, sin necesidad de una app de mapas aparte.",
      },
      {
        title: "Cuenta atrás de horas restantes por proyecto",
        body: "Define las horas previstas al abrir el proyecto. Los trabajadores ven cuántas horas quedan; tú ves el estado en directo en tu panel. Los sobrecostes se ven antes de que ocurran.",
        plan: "Project Basic",
      },
      {
        title: "Fichaje del trabajador por proyecto",
        body: "Cada trabajador ficha en el proyecto correcto. Siempre sabes quién está en qué obra ahora mismo.",
        plan: "Project Basic",
      },
      {
        title: "Registro de trabajo extra y materiales",
        body: "Cuando el alcance crece en la obra, los trabajadores registran trabajo extra y materiales en tiempo real —con texto y fotos— para que nada se facture de memoria.",
        plan: "Project Basic",
      },
    ],
    compliance: {
      label: "Registro horario digital",
      title: "Fichaje verificado por ubicación por proyecto para equipos de instalación",
      body: "Done-it da a los equipos de instalación fichaje verificado por ubicación por proyecto, una cuenta atrás de horas restantes en tiempo real y exportación de nómina automática, cubriendo cualquier requisito local de registro horario. Para equipos que trabajan en obras de construcción belgas, también se integra con CheckIn@Work.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "El trabajador abre el proyecto en su móvil", body: "Dirección, contacto, alcance y archivos subidos están ahí mismo. Navega con un toque, sin llamadas a la oficina para pedir indicaciones." },
      { step: "Las horas se registran automáticamente en el trabajo correcto", body: "El fichaje vincula cada minuto trabajado al proyecto correcto. El presupuesto restante se actualiza en tiempo real." },
      { step: "El trabajo extra se captura en la obra", body: "Los cambios de alcance, materiales extra y retrasos se registran con texto y fotos en el momento en que ocurren, listos para la factura." },
    ],
    stats: [
      { value: "Tiempo real", label: "vista de horas restantes por proyecto" },
      { value: "0", label: "llamadas a la oficina para pedir direcciones de obra" },
      { value: "1 clic", label: "navega a cualquier dirección de proyecto desde la app" },
    ],
    quote: {
      text: "Las horas caen en el proyecto correcto automáticamente. Calcular el coste de cada trabajo es de repente fácil: vemos el margen real antes de cerrar un trabajo.",
      who: "Propietario, empresa de instalación · 6 empleados",
      result: "Coste real por trabajo, en directo",
    },
    relatedSlugs: ["construction", "electricians", "hvac"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "field-service",
    name: "Servicio de campo",
    headline: "Control horario para equipos de servicio de campo: kilometraje, gastos y horas desde la carretera, todo en una sola app",
    intro: "Técnicos que visitan a varios clientes al día, tickets en la guantera, kilometraje anotado de memoria. Done-it captura horas, kilómetros y gastos en el campo, vinculados al trabajo correcto en el momento en que ocurren.",
    pains: [
      {
        title: "Kilometraje anotado días después, si acaso",
        body: "Pedir a los técnicos que reconstruyan sus rutas al final de la semana implica reembolsos inexactos, disputas con clientes y un montón cada vez mayor de kilómetros sin reclamar.",
      },
      {
        title: "Gastos en tickets de papel que desaparecen",
        body: "Un ticket en la guantera es un coste que puede que nunca llegue a una factura. El coste real por trabajo siempre se queda corto.",
      },
      {
        title: "Sin vista en tiempo real de dónde está el equipo",
        body: "Sin fichajes en directo, solo descubres dónde está un técnico cuando algo va mal y alguien llama.",
      },
      {
        title: "Difícil ver el margen real por trabajo",
        body: "Cuando la mano de obra, los desplazamientos y los gastos se controlan en sistemas separados —o no se controlan—, nunca hay una cifra de coste única por trabajo.",
      },
    ],
    features: [
      {
        title: "Captura automática de kilometraje (Trax-it)",
        body: "El GPS registra salida, llegada y distancia exacta por trayecto. Configura tipos de transporte (coche propio, vehículo de empresa, coche compartido) con sus propias tarifas de reembolso. Sin cuaderno manual.",
        plan: "Add-on",
      },
      {
        title: "Gestión de gastos en la obra",
        body: "Los trabajadores fotografían un ticket en el campo; queda vinculado al trabajo y listo para el reembolso, sin perseguirlo a fin de mes.",
        plan: "Time Basic",
      },
      {
        title: "Control de costes vinculado al proyecto",
        body: "Horas, kilometraje y gastos se acumulan contra el trabajo en tiempo real. El coste por trabajo es visible mientras el trabajo sigue abierto.",
        plan: "Project Basic",
      },
      {
        title: "Fichaje verificado por ubicación y vista del equipo en directo",
        body: "Cada técnico ficha en la sede del cliente. Ves quién está dónde, ahora mismo: sin llamadas, sin conjeturas.",
      },
      {
        title: "Exportación de nómina con un clic",
        body: "Las horas y los reembolsos de kilometraje se exportan limpios para tu gestoría de nóminas, con tus tarifas de kilometraje configuradas aplicadas automáticamente.",
      },
    ],
    compliance: {
      label: "Control digital de horas y kilometraje",
      title: "Horas por GPS y kilometraje automático para equipos de servicio de campo",
      body: "Done-it captura el kilometraje automáticamente por trayecto, aplica tus tarifas de reembolso configuradas y registra cada kilómetro al cliente o proyecto correcto. La sentencia Tyco de la UE implica que el desplazamiento de casa al primer cliente cuenta como tiempo de trabajo: Done-it también lo controla.",
      slug: "how-to-track-mileage-for-field-teams",
    },
    workflow: [
      { step: "El técnico sale hacia el primer aviso", body: "El fichaje inicia el registro del trayecto. Los kilómetros se capturan automáticamente desde la salida hasta la llegada, sin entrada manual." },
      { step: "En el lugar de trabajo", body: "Las horas se vinculan al proyecto correcto. El trabajo extra, los materiales y los tickets se registran en la app en el momento en que ocurren." },
      { step: "Cierre de mes sin el caos", body: "Cada hora, kilómetro y gasto ya está vinculado al trabajo correcto. Exporta a la nómina y factura con cifras de coste reales." },
    ],
    stats: [
      { value: "Automática", label: "captura de kilometraje: sin cuaderno manual" },
      { value: "En directo", label: "coste por trabajo: horas + desplazamientos + gastos" },
      { value: "0", label: "kilómetros perdidos: cada trayecto capturado automáticamente" },
    ],
    quote: {
      text: "El kilometraje era un quebradero de cabeza mensual. Ahora se registra solo: cada kilómetro queda vinculado al cliente correcto antes incluso de volver a la oficina.",
      who: "Responsable de operaciones, empresa de servicio de campo · 11 empleados",
      result: "Coste por trabajo preciso, siempre",
    },
    relatedSlugs: ["hvac", "maintenance", "plumbing"],
    relatedArticles: [
      "how-to-track-mileage-for-field-teams",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },

  {
    slug: "hvac",
    name: "Climatización (HVAC)",
    headline: "Control horario para técnicos de climatización: cálculo de costes por trabajo, piezas y desplazamientos en una sola app",
    intro: "Cada aviso de servicio tiene tres costes: mano de obra, piezas y desplazamiento. Done-it captura los tres en el campo y los acumula contra el trabajo correcto, para que conozcas tu margen antes de cerrar la factura.",
    pains: [
      {
        title: "Es difícil calcular el coste exacto de los avisos de servicio",
        body: "La mano de obra, las piezas y el desplazamiento viven en sistemas diferentes, o en bolsillos diferentes. El coste real de una salida solo queda claro mucho después de que el técnico haya abandonado el lugar.",
      },
      {
        title: "Sin prueba del tiempo de servicio",
        body: "Cuando un cliente discute cuánto duró una reparación, un parte horario en papel es fácil de impugnar. Las marcas de tiempo de fichaje de entrada y salida confirmadas por GPS no lo son.",
      },
      {
        title: "Los contratos de mantenimiento erosionan el margen en silencio",
        body: "En los contratos de mantenimiento recurrente, los pequeños sobrecostes se acumulan a lo largo de muchas visitas. Sin un contador de horas en directo, solo descubres que el contrato está en números rojos en la renovación.",
      },
      {
        title: "Los técnicos olvidan registrar las pausas",
        body: "Las entradas de tiempo manuales se saltan las pausas, lo que lleva a pagar de más y a una nómina que no coincide con la realidad.",
      },
    ],
    features: [
      {
        title: "Control horario basado en proyectos",
        body: "Cada aviso de servicio o contrato de mantenimiento es un proyecto. Horas, desplazamientos y materiales se acumulan en tiempo real, dándote visibilidad del margen en directo.",
        plan: "Project Basic",
      },
      {
        title: "Trabajo extra y materiales, registrados en la obra",
        body: "Cuando un trabajo crece, los técnicos registran el trabajo extra y las piezas con texto y fotos antes de irse. Nada se factura de memoria.",
        plan: "Project Basic",
      },
      {
        title: "Kilometraje y costes de transporte",
        body: "Los kilómetros por salida se registran automáticamente y se imputan al trabajo. Configura las tarifas de reembolso por tipo de vehículo.",
        plan: "Add-on",
      },
      {
        title: "Deducción automática de pausas",
        body: "Las pausas obligatorias se restan automáticamente del tiempo total: sin disputas, sin errores de nómina.",
      },
      {
        title: "Fichaje verificado por ubicación y prueba de presencia",
        body: "Fichaje con marca de tiempo y confirmado por GPS en cada sede. Cada registro se verifica por ubicación.",
      },
    ],
    compliance: {
      label: "Control digital de horas y costes",
      title: "Cálculo completo de costes por trabajo para avisos de servicio de climatización",
      body: "Done-it da a los equipos de climatización fichaje verificado por ubicación por aviso de servicio, visibilidad de costes en directo por trabajo (mano de obra + desplazamiento + piezas) y una exportación de nómina limpia, cumpliendo cualquier requisito local de registro horario sin hardware adicional.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "El técnico ficha en la sede", body: "El GPS marca la hora de llegada y vincula las horas al aviso de servicio o contrato de mantenimiento. Sin papel, sin terminal." },
      { step: "El trabajo extra se captura sobre la marcha", body: "Las piezas utilizadas, las horas extra y los cambios de alcance se registran en la app con fotos, antes de abandonar la sede." },
      { step: "El coste del trabajo es visible de inmediato", body: "Mano de obra, desplazamiento y materiales se suman contra el proyecto en tiempo real. Ves el margen antes de emitir la factura." },
    ],
    stats: [
      { value: "3", label: "tipos de coste capturados por aviso: mano de obra, desplazamiento, piezas" },
      { value: "En directo", label: "visibilidad del margen por contrato de servicio" },
      { value: "0", label: "sorpresas al facturar: costes visibles en tiempo real" },
    ],
    quote: {
      text: "Por fin sabemos lo que nos cuesta realmente cada contrato de servicio. Las sorpresas en el margen pararon el día que empezamos a usar Done-it.",
      who: "Propietario, empresa de climatización · 7 técnicos",
      result: "Coste real por aviso de servicio, en directo",
    },
    relatedSlugs: ["installation", "electricians", "plumbing"],
    relatedArticles: [
      "how-to-track-mileage-for-field-teams",
      "time-tracking-for-field-teams-complete-guide",
    ],
  },

  {
    slug: "electricians",
    name: "Electricistas",
    headline: "Control horario para electricistas: horas por proyecto, control de presupuesto y navegación en una sola app",
    intro: "Horas por proyecto, una cuenta atrás en directo del presupuesto restante y navegación a la obra con un botón: hecho para pequeños equipos de electricistas que necesitan control del proyecto sin la carga administrativa.",
    pains: [
      {
        title: "Las horas no se vinculan al proyecto correcto",
        body: "Cuando los electricistas trabajan en varios trabajos en una semana, asignar las horas manualmente al final del día genera costes mal atribuidos, disputas de facturación y una nómina que no puedes explicar.",
      },
      {
        title: "Sobrecostes detectados demasiado tarde",
        body: "Sin una vista en directo de las horas consumidas frente a las presupuestadas, descubres que un proyecto se ha pasado al emitir la factura, no cuando aún podías reaccionar.",
      },
      {
        title: "Tiempo perdido buscando la obra",
        body: "Direcciones tecleadas en una app aparte, llamadas a la oficina para pedir indicaciones, llegadas tarde a las sedes de los clientes. Cada minuto perdido es un minuto facturable perdido.",
      },
      {
        title: "Difícil cuadrar las horas de subcontratistas",
        body: "En instalaciones mayores, los electricistas subcontratados controlan sus propias horas por separado. Conseguir una vista combinada de los costes de mano de obra al final de un proyecto es un trabajo manual y propenso a errores.",
      },
    ],
    features: [
      {
        title: "Horas de proyecto con presupuesto restante en directo",
        body: "Cada minuto trabajado se vincula automáticamente al proyecto correcto. Una cuenta atrás en directo muestra cuántas horas quedan frente al presupuesto, para ti y para el trabajador en la obra.",
        plan: "Project Basic",
      },
      {
        title: "Navegación a la obra con un botón",
        body: "La dirección del proyecto está en la app. Un toque abre la navegación: sin buscar, sin app de mapas aparte, sin llamadas a la oficina.",
      },
      {
        title: "Fichaje del trabajador por proyecto",
        body: "Cada electricista ficha en el trabajo correcto. Siempre sabes quién está en qué obra ahora mismo.",
        plan: "Project Basic",
      },
      {
        title: "Trabajo extra registrado en la obra",
        body: "Cuando cambia el alcance —un circuito inesperado, materiales adicionales—, se registra en la app con fotos, antes de que la cuadrilla se vaya.",
        plan: "Project Basic",
      },
      {
        title: "Control de horas extra con compensación flexible",
        body: "Las horas extra se registran en el momento en que ocurren. Los trabajadores eligen entre cobrarlas o convertirlas en tiempo libre.",
        plan: "Time Pro",
      },
    ],
    compliance: {
      label: "Fichaje verificado por ubicación por proyecto",
      title: "Control de presupuesto en directo y fichaje verificado por ubicación para equipos de electricistas",
      body: "Done-it da a los electricistas fichaje verificado por ubicación por proyecto, un presupuesto de horas restantes en directo y exportación de nómina, satisfaciendo cualquier requisito local de registro horario. Para equipos en obras de construcción belgas, también se integra con CheckIn@Work.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "El electricista navega hasta el trabajo", body: "La dirección y el alcance ya están en la app. Un toque abre la navegación: sin llamadas, sin confusión." },
      { step: "Las horas se vinculan al proyecto correcto automáticamente", body: "El fichaje en la obra vincula cada minuto trabajado al proyecto correcto. El contador de horas restantes se actualiza en directo." },
      { step: "Los cambios de alcance quedan registrados", body: "El trabajo extra y los materiales se registran en la obra, antes de irse. La factura refleja lo que realmente ocurrió." },
    ],
    stats: [
      { value: "En directo", label: "vista de horas restantes por proyecto" },
      { value: "0", label: "llamadas a la oficina para pedir direcciones de obra" },
      { value: "1 toque", label: "para fichar en cualquier obra: sin terminal" },
    ],
    quote: {
      text: "Sabemos en tiempo real si un proyecto se está pasando de presupuesto. Solo eso nos ha librado de dos malas sorpresas este año.",
      who: "Propietario, empresa de electricidad · 5 electricistas",
      result: "Sobrecostes detectados antes de que ocurran",
    },
    relatedSlugs: ["installation", "hvac", "plumbing"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "how-to-track-mileage-for-field-teams",
    ],
  },

  {
    slug: "plumbing",
    name: "Fontanería",
    headline: "Control horario para equipos de fontanería: fichaje rápido, nómina precisa y prueba de presencia en cada salida",
    intro: "Salidas de urgencia, varios trabajos al día, horas rellenadas de memoria al final de la semana, o nada. Done-it lo convierte en un trabajo de un toque en cada sede y una nómina de un clic a fin de mes.",
    pains: [
      {
        title: "Horas de salida reconstruidas de memoria",
        body: "Con varios trabajos en un día, los fontaneros rara vez tienen tiempo de rellenar partes horarios precisos. Las horas se redondean o se olvidan, y la nómina es, en el mejor de los casos, una estimación.",
      },
      {
        title: "Sin prueba del tiempo en la sede",
        body: "Cuando un cliente discute cuánto duró una reparación, no hay un registro objetivo. Un fichaje digital confirmado por GPS es fácil de aportar; un parte horario en papel no.",
      },
      {
        title: "Kilometraje entre trabajos sin controlar",
        body: "El desplazamiento entre salidas se acumula a lo largo de la semana y del mes, pero sin captura automática rara vez se factura o reembolsa correctamente.",
      },
      {
        title: "La preparación de la nómina es lenta",
        body: "A fin de mes, perseguir partes horarios, corregir errores y construir la exportación de la nómina lleva un tiempo que tu equipo administrativo no debería estar dedicando.",
      },
    ],
    features: [
      {
        title: "Fichaje verificado por ubicación de un toque en cada salida",
        body: "Llegada y salida registradas con un toque, con verificación de la ubicación. Lo bastante rápido para urgencias; lo bastante preciso para disputas.",
        plan: "Time Basic",
      },
      {
        title: "Deducción automática de pausas",
        body: "Las pausas obligatorias se restan del tiempo total automáticamente: nada que rellenar, sin errores de nómina.",
        plan: "Time Basic",
      },
      {
        title: "Kilometraje capturado por trayecto",
        body: "Configura tipos de transporte y tarifas de reembolso. Los kilómetros se registran automáticamente y se vinculan al cliente o proyecto correcto.",
        plan: "Add-on",
      },
      {
        title: "Gestión de ausencias y justificantes médicos",
        body: "Los trabajadores envían justificantes médicos digitalmente desde la app. Crea los tipos de ausencia que tu equipo realmente usa, incluidos los retrasos por mal tiempo.",
      },
      {
        title: "Exportación de nómina con un clic",
        body: "Todos los datos de horas y kilometraje se exportan limpios para tu gestoría de nóminas: sin preparación manual, sin Excel.",
        plan: "Time Basic",
      },
    ],
    compliance: {
      label: "Registro horario digital",
      title: "Fichaje verificado por ubicación rápido en cada salida",
      body: "Done-it cubre cualquier requisito local de registro horario para equipos de fontanería: fichaje verificado por ubicación de un toque por salida, deducción automática de pausas y una exportación de nómina limpia. Sin partes horarios en papel, sin reconstrucción manual.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "El fontanero llega a la salida", body: "Un toque ficha. El GPS registra la ubicación y la hora. Lo bastante rápido para un trabajo de urgencia." },
      { step: "La salida se ficha al irse", body: "El fichaje de salida cierra el registro de tiempo. El kilometraje hasta el siguiente trabajo empieza a registrarse automáticamente." },
      { step: "El cierre de mes es un clic", body: "Todas las salidas, horas y kilómetros se exportan limpios. La preparación de la nómina pasa de un día a minutos." },
    ],
    stats: [
      { value: "1 toque", label: "para fichar: lo bastante rápido para urgencias" },
      { value: "1 clic", label: "exportación de nómina a tu gestoría" },
      { value: "0", label: "horas reconstruidas de memoria a fin de mes" },
    ],
    quote: {
      text: "Antes pasábamos una mañana a fin de mes reconstruyendo horas. Ahora solo exportamos y enviamos. La gestoría de nóminas lo recibe limpio.",
      who: "Propietario, empresa de fontanería · 4 fontaneros",
      result: "Preparación de nómina reducida de horas a minutos",
    },
    relatedSlugs: ["hvac", "electricians", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "how-to-track-mileage-for-field-teams",
    ],
  },

  {
    slug: "landscaping",
    name: "Jardinería y paisajismo",
    headline: "Control horario para empresas de jardinería: cuadrillas de temporada, múltiples obras y kilometraje en una sola vista",
    intro: "La temporada alta significa muchas obras, cuadrillas cambiantes y muchos kilómetros. Done-it mantiene organizada cada hora, cada ausencia y cada trayecto, para que puedas centrarte en el trabajo, no en el papeleo.",
    pains: [
      {
        title: "Las cuadrillas de temporada son complejas de gestionar",
        body: "Trabajadores temporales, horarios cambiantes y tamaños de equipo variables hacen que el control horario preciso sea casi imposible con papel. Para cuando cuadras a fin de mes, siempre falta algo.",
      },
      {
        title: "Horas repartidas entre muchas obras",
        body: "Una cuadrilla que visita tres o cuatro obras al día genera horas que hay que repartir correctamente. La entrada manual lleva a una mala atribución y a una nómina impugnada.",
      },
      {
        title: "Las paradas por mal tiempo son difíciles de documentar",
        body: "La parada por mal tiempo es un evento real en jardinería. Sin un registro digital, documentar las paradas y ajustar el pago es lento y propenso a errores.",
      },
      {
        title: "El kilometraje entre obras se queda sin controlar",
        body: "Furgonetas de empresa, vehículos personales y coches compartidos cruzan entre obras todo el día. Sin captura automática, esos kilómetros nunca llegan a la imagen de costes.",
      },
    ],
    features: [
      {
        title: "Vista de cuadrillas multiobra",
        body: "Ve todas las cuadrillas, todas las obras en tiempo real. Sabe quién trabaja dónde, ahora mismo, no a fin de mes.",
      },
      {
        title: "Planificación de turnos para equipos de temporada",
        body: "Planifica quién trabaja dónde, gestiona cambios de última hora y envía el horario a los móviles del equipo, sin una llamada a cada persona.",
        plan: "Time Pro",
      },
      {
        title: "Tipos de ausencia personalizados",
        body: "Crea tipos de ausencia personalizados para tu sector: paradas estacionales por mal tiempo, permiso parental, días de baja. Los trabajadores suben la documentación digitalmente desde la app.",
      },
      {
        title: "Kilometraje con control de coche compartido",
        body: "Configura tipos de transporte (furgoneta de empresa, coche personal, conductor frente a pasajero en coche compartido) con sus propias tarifas. Los kilómetros se registran automáticamente por trayecto.",
        plan: "Add-on",
      },
      {
        title: "Control de horas extra con conversión en días libres",
        body: "Las horas extra de temporada alta se acumulan rápido. Cada trabajador puede elegir entre cobrarlas o acumular las horas como tiempo libre, controlado de forma transparente.",
        plan: "Time Pro",
      },
    ],
    compliance: {
      label: "Registro horario digital",
      title: "Fichaje verificado por ubicación y control de ausencias para equipos de jardinería de temporada",
      body: "Done-it da a los equipos de jardinería registro horario digital en todas las obras y para el personal de temporada: fichaje verificado por ubicación, tipos de ausencia personalizados para paradas por mal tiempo y una exportación de nómina limpia que satisface cualquier requisito local.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "La cuadrilla ficha en la primera obra", body: "El GPS registra la llegada. Las horas se reparten automáticamente a la ubicación y el proyecto correctos, sin asignación manual al final del día." },
      { step: "El mal tiempo detiene el trabajo", body: "El encargado registra la parada desde la app: un registro digital limpio, sin papeleo extra." },
      { step: "Cierre de mes: todas las obras, toda la cuadrilla, una exportación", body: "Horas, ausencias y kilometraje en cada obra y de cada miembro de la cuadrilla se exportan limpios para la nómina." },
    ],
    stats: [
      { value: "Automática", label: "captura de kilometraje: se acabó el cuaderno" },
      { value: "En directo", label: "vista de cuadrillas en todas las obras" },
      { value: "1 clic", label: "exportación de nómina para todas las obras y toda la cuadrilla" },
    ],
    quote: {
      text: "La temporada alta solía significar caos en la nómina. Ahora nuestro encargado revisa la app, todo el mundo está contabilizado, y yo exporto las horas el último día del mes.",
      who: "Propietario, empresa de jardinería · 12 trabajadores de temporada",
      result: "Se acabó la reconstrucción de nómina a fin de mes",
    },
    relatedSlugs: ["maintenance", "construction", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },

  {
    slug: "maintenance",
    name: "Mantenimiento",
    headline: "Control horario para equipos de mantenimiento: visitas recurrentes, coste por contrato y prueba de presencia en una sola app",
    intro: "Los contratos de mantenimiento recurrente viven o mueren por la visibilidad de costes. Done-it registra cada visita, controla las horas frente al presupuesto del contrato y te da —a ti y a tus clientes— prueba de presencia en cada aviso.",
    pains: [
      {
        title: "Sin visibilidad de costes por contrato de mantenimiento",
        body: "Cuando las horas de las visitas recurrentes no se controlan frente al contrato concreto, no sabes si un cliente es rentable hasta la revisión de fin de año, momento en el que ya es demasiado tarde para reajustar el precio.",
      },
      {
        title: "Los clientes esperan prueba de lo que se hizo",
        body: "Los gestores de instalaciones y los propietarios quieren cada vez más registros de presencia: quién vino, cuándo llegó, qué hizo. Un registro en papel presentado semanas después no satisface los requisitos del SLA.",
      },
      {
        title: "Las visitas recurrentes son difíciles de planificar y registrar",
        body: "Los turnos rotativos en muchas obras recurrentes —con personal que se cubre entre sí— crean un rompecabezas de planificación y control horario que los procesos manuales no pueden resolver con fiabilidad.",
      },
      {
        title: "Las paradas por mal tiempo también afectan al mantenimiento en exteriores",
        body: "El mantenimiento de zonas verdes, de fachadas y de instalaciones exteriores se enfrenta a paradas por mal tiempo. Documentarlas correctamente requiere un rastro digital.",
      },
    ],
    features: [
      {
        title: "Control de costes basado en contratos",
        body: "Cada contrato de mantenimiento es un proyecto. Las horas se registran automáticamente contra él, dándote una cifra de coste en directo y una cuenta atrás de horas restantes por cada cliente.",
        plan: "Project Basic",
      },
      {
        title: "Prueba de presencia confirmada por GPS",
        body: "Los fichajes con marca de tiempo y verificados por ubicación son un registro de quién estuvo en la sede, cuándo y durante cuánto tiempo, listo para compartir con los clientes cuando lo pidan.",
      },
      {
        title: "Actualizaciones con fotos desde la obra",
        body: "Los trabajadores reportan incidencias, tareas completadas o trabajo extra directamente desde la app, con texto y fotos. Los clientes obtienen un registro en tiempo real; tú tienes la documentación para facturar.",
        plan: "Project Basic",
      },
      {
        title: "Planificación de turnos recurrentes",
        body: "Planifica las visitas recurrentes en todos los contratos, asigna trabajadores y gestiona las sustituciones, sin una llamada a cada persona.",
        plan: "Time Pro",
      },
      {
        title: "Tipos de ausencia para mantenimiento en exteriores",
        body: "Configura paradas por mal tiempo y otros tipos de ausencia. Los trabajadores los registran desde la app; la documentación se sube digitalmente.",
      },
    ],
    compliance: {
      label: "Control horario basado en contratos",
      title: "Visibilidad de costes en directo por contrato de mantenimiento",
      body: "Done-it da a los equipos de mantenimiento prueba de presencia confirmada por GPS por contrato, tipos de ausencia personalizados y una vista de costes en directo por cada cliente recurrente, satisfaciendo cualquier requisito local de registro horario sin hardware adicional.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "El trabajador llega a la obra de mantenimiento", body: "El fichaje verificado por ubicación registra la hora de llegada y vincula las horas al contrato correcto. La prueba de presencia se crea automáticamente." },
      { step: "Tareas completadas, incidencias registradas", body: "El trabajo completado y cualquier incidencia se reportan desde la app con fotos. Los clientes pueden ver exactamente qué se hizo." },
      { step: "Coste por contrato visible de inmediato", body: "Las horas se acumulan contra el presupuesto del contrato en tiempo real. Conoces el margen antes de que el contrato se renueve." },
    ],
    stats: [
      { value: "En directo", label: "coste por contrato de mantenimiento" },
      { value: "GPS", label: "prueba de presencia lista para clientes cuando lo pidan" },
      { value: "0", label: "renovaciones de contrato sin conocer tu margen" },
    ],
    quote: {
      text: "Podemos decirle a un gestor de instalaciones exactamente quién estuvo en la sede y cuándo, en dos minutos. Ese nivel de prueba cambió cómo nos ven los clientes.",
      who: "Responsable de operaciones, empresa de mantenimiento · 8 trabajadores",
      result: "Disputas con clientes reducidas a cero",
    },
    relatedSlugs: ["cleaning", "landscaping", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },
  {
    slug: "retail",
    name: "Tiendas y comercio",
    headline: "Control horario para tiendas: fichaje por turnos, coberturas y una exportación lista para nóminas, en una sola app",
    intro: "Turnos de apertura, turnos de cierre y siempre alguien cubriendo a alguien. Done-it le da a cada tienda un registro digital limpio de quién trabajó cada turno: fichado desde su propio móvil y listo para la nómina a fin de mes.",
    pains: [
      {
        title: "Los cambios de turno nunca llegan a la nómina",
        body: "Alguien cubre a un compañero enfermo, otro se queda una hora más al cierre. Se apunta en una nota, o no se apunta. Y la nómina ya sale mal antes de empezar.",
      },
      {
        title: "Varias tiendas, una sola nómina",
        body: "El personal se mueve entre locales y los de jornada parcial trabajan en dos sitios. Recoger las horas de cada tienda y unirlas a mano cuesta un día cada mes.",
      },
      {
        title: "Descansos y horas extra a ojo",
        body: "Los descansos obligatorios se descuentan de memoria y las horas extra se arreglan de palabra: justo lo primero que pregunta una inspección.",
      },
    ],
    features: [
      {
        title: "Fichaje en la propia tienda",
        body: "Un toque en su móvil al empezar el turno. Sin terminal en la caja, sin tarjeta compartida, sin hoja en la puerta del almacén.",
      },
      {
        title: "Horas por tienda, personal entre locales",
        body: "Cada tienda es un proyecto. Quien cubre en otro local ficha en ese local: ves las horas donde se trabajaron de verdad.",
      },
      {
        title: "Descansos descontados automáticamente",
        body: "Configuras la regla de descanso una vez y se aplica a todos los turnos. Las horas extra se registran en el momento y cada empleado elige entre cobrarlas o compensarlas.",
      },
    ],
    compliance: {
      label: "Registro horario por turnos",
      title: "Un registro defendible de cada turno trabajado",
      body: "Done-it guarda un registro digital y objetivo de cada entrada y salida, con descansos y horas extra: el registro que pide una auditoría o una reclamación de nómina.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "El equipo ficha su turno", body: "Un toque en el móvil al empezar. La cobertura en otro local se ficha en ese local." },
      { step: "Ves quién está, tienda a tienda", body: "Una vista en directo de todos los locales: detectas un turno de apertura sin cubrir antes de abrir." },
      { step: "Nómina en una exportación", body: "Horas, descansos y horas extra por empleado y por tienda, exportados listos para tu proveedor de nóminas." },
    ],
    stats: [
      { value: "1 toque", label: "para fichar, sin terminal en la caja" },
      { value: "En directo", label: "quién está trabajando, en todas las tiendas" },
      { value: "0", label: "cambios de turno perdidos entre la tienda y la nómina" },
    ],
    quote: {
      text: "Dos tiendas y mucha jornada parcial. Antes, la mitad de las coberturas se ajustaban de memoria. Ahora cada hora está en la tienda correcta y la nómina lleva minutos.",
      who: "Propietaria, comercio · 11 empleados",
      result: "La nómina pasó de un día a menos de una hora",
    },
    relatedSlugs: ["hospitality", "beauty", "offices"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "offices",
    name: "Oficinas y servicios profesionales",
    headline: "Control horario para oficinas: horas por cliente y proyecto, sin hojas de cálculo",
    intro: "Asesorías, notarías, estudios de arquitectura, agencias y consultoras facturan su tiempo, o al menos necesitan saber a dónde fue. Done-it registra las horas en el cliente y el proyecto correctos, desde el escritorio o fuera de la oficina.",
    pains: [
      {
        title: "Horas facturables reconstruidas el viernes",
        body: "El tiempo que se apunta al final de la semana es tiempo estimado. Horas enteras desaparecen en silencio entre el trabajo y la factura.",
      },
      {
        title: "Nadie sabe cuánto cuesta un cliente",
        body: "Los honorarios se acordaron hace meses. Si el trabajo sigue cabiendo dentro solo se ve cuando alguien lo suma, y normalmente ya es tarde.",
      },
      {
        title: "El trabajo híbrido deja el registro incompleto",
        body: "Parte del equipo está en la oficina, parte en casa y parte en el cliente. La presencia deja de ser un dato y pasa a ser una reconstrucción.",
      },
    ],
    features: [
      {
        title: "Horas por cliente y proyecto",
        body: "Cada registro cae en un proyecto, así que ves las horas por cliente sin exportar nada ni preguntar a nadie.",
      },
      {
        title: "Funciona en el escritorio y fuera",
        body: "La misma app en el móvil o en el navegador. Visitar a un cliente cuenta como trabajo para ese cliente, incluido el desplazamiento si así lo quieres.",
      },
      {
        title: "Horas extra y vacaciones en un solo sitio",
        body: "Las horas extra se registran en el momento; las vacaciones se solicitan y se aprueban en la app. Sin hoja de cálculo paralela de quién está fuera y cuándo.",
      },
    ],
    compliance: {
      label: "Registro horario por cliente",
      title: "Un registro objetivo de las horas trabajadas",
      body: "Done-it mantiene un registro digital y a prueba de manipulaciones del tiempo de trabajo de cada empleado: la base tanto para la nómina como para una factura defendible ante el cliente.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Abre un proyecto de cliente", body: "Se configura una vez con el cliente y, si facturas por presupuesto, las horas previstas." },
      { step: "El equipo imputa su tiempo", body: "Desde el escritorio o el móvil. Cada hora cae en un cliente, no en la memoria de alguien." },
      { step: "Ves las horas por cliente y exportas para nóminas", body: "Totales en directo por proyecto y una exportación limpia para la nómina o la facturación a fin de mes." },
    ],
    stats: [
      { value: "Por cliente", label: "horas visibles mientras el trabajo sigue en marcha" },
      { value: "Oficina o calle", label: "el mismo registro, en móvil y navegador" },
      { value: "1 exportación", label: "para nóminas y facturación" },
    ],
    quote: {
      text: "Antes reconstruíamos la semana cada viernes. Ahora las horas ya están en el cliente correcto y veo qué expedientes se están comiendo los honorarios.",
      who: "Socio, asesoría contable · 8 empleados",
      result: "Horas facturables recuperadas cada mes",
    },
    relatedSlugs: ["retail", "healthcare", "wellness"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "beauty",
    name: "Salones y estética",
    headline: "Control horario para salones: turnos, jornada parcial y nóminas sin agenda de papel",
    intro: "Peluquerías, barberías y centros de uñas y estética funcionan con turnos y contratos a tiempo parcial. Done-it registra quién trabajó y cuándo, descuenta los descansos automáticamente y entrega a nóminas un archivo limpio.",
    pains: [
      {
        title: "Contratos parciales, variaciones sin fin",
        body: "Tres días una semana, cuatro la siguiente, un sábado cambiado por un martes. Las horas del contrato y las trabajadas se separan rápido.",
      },
      {
        title: "La agenda no es un registro horario",
        body: "Las citas dicen qué se reservó, no quién trabajó. Cuando lo pregunta la nómina o una inspección, el sistema de reservas es la respuesta equivocada.",
      },
      {
        title: "Las horas extra se arreglan de palabra",
        body: "Quedarse hasta tarde por una última clienta se recuerda, se promete y a veces se olvida. Ese es el hueco que acaba en conflicto.",
      },
    ],
    features: [
      {
        title: "Fichaje en el salón",
        body: "Un toque en su propio móvil al empezar el turno: sin terminal compartido en recepción, sin lista de firmas junto al espejo.",
      },
      {
        title: "Descansos y jornada parcial, una sola vez",
        body: "Configura la regla de descanso y la jornada estándar una vez; se aplican a todos los turnos, con excepciones por persona cuando el contrato es distinto.",
      },
      {
        title: "Horas extra: cobradas o compensadas",
        body: "Las horas extra se registran según se trabajan y cada empleado elige entre cobrarlas o compensarlas con tiempo libre. Se cierra en la app, no de memoria.",
      },
    ],
    compliance: {
      label: "Registro horario digital",
      title: "Un registro que va más allá del libro de citas",
      body: "Done-it registra el tiempo de trabajo real de cada empleado, con descansos y horas extra: separado de las reservas y defendible cuando lo revisan.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "El equipo ficha su turno", body: "Un toque al llegar. Los descansos se descuentan con la regla que configuraste una vez." },
      { step: "Ves el día según avanza", body: "Quién está, quién está en su descanso, quién se quedó hasta tarde. En directo y sin preguntar." },
      { step: "Nóminas recibe un archivo limpio", body: "Horas trabajadas, descansos y horas extra por empleado, exportados para tu proveedor de nóminas." },
    ],
    stats: [
      { value: "1 toque", label: "para fichar, sin nada compartido ni en papel" },
      { value: "Auto", label: "descuento de descansos en cada turno" },
      { value: "0", label: "horas de más ajustadas de memoria" },
    ],
    quote: {
      text: "Todo el mundo es de jornada parcial y cada semana es distinta. La app simplemente lo registra: he dejado de rehacer el mes en una hoja de cálculo.",
      who: "Propietaria, peluquería · 6 empleados",
      result: "Se acabaron las horas discutidas a fin de mes",
    },
    relatedSlugs: ["wellness", "retail", "hospitality"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "wellness",
    name: "Salud y bienestar",
    headline: "Control horario para gimnasios, estudios y spas: turnos, clases y nóminas en una app",
    intro: "Gimnasios, estudios de fitness, clínicas de fisioterapia y spas abren muchas horas con personal rotativo. Done-it registra quién cubrió cada turno, descuenta los descansos automáticamente y exporta la nómina sin líos.",
    pains: [
      {
        title: "Horarios largos, coberturas rotativas",
        body: "Turnos de mañana, clases de tarde y fines de semana, cubiertos por una mezcla de jornada completa, parcial y autónomos. El cuadrante es un plan, no un registro.",
      },
      {
        title: "Las horas de clase y de sala se mezclan",
        body: "Un instructor da dos clases y cubre recepción entre medias. Pagar eso bien exige saber qué se trabajó de verdad, no qué estaba programado.",
      },
      {
        title: "Varios centros, una sola administración",
        body: "El personal se mueve entre estudios. Unir sus horas a mano cada mes es lento y da errores.",
      },
    ],
    features: [
      {
        title: "Fichaje por centro",
        body: "Un toque en su móvil en el centro donde trabaja. La cobertura en otro estudio cae en las horas de ese estudio.",
      },
      {
        title: "Vista en directo de quién está",
        body: "Ves la sala de todos los centros en tiempo real: un turno de mañana sin cubrir se ve antes de abrir, no después.",
      },
      {
        title: "Descansos y horas extra resueltos",
        body: "Reglas de descanso aplicadas automáticamente y horas extra registradas en el momento, con cobro o compensación en tiempo libre según el empleado.",
      },
    ],
    compliance: {
      label: "Registro de turnos multicentro",
      title: "Un registro objetivo de cada turno y cada centro",
      body: "Done-it mantiene un registro digital del tiempo de trabajo por empleado y por centro: la base para pagar correctamente y para cualquier revisión de las horas trabajadas.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "El equipo ficha en el estudio", body: "Un toque al empezar el turno, en el centro donde trabaja." },
      { step: "Las coberturas se ven en directo", body: "Quién está en cada centro ahora mismo, para cubrir los huecos antes de que los socios lo noten." },
      { step: "Una exportación para nóminas", body: "Horas, descansos y horas extra por empleado y por estudio, listos para el proveedor de nóminas." },
    ],
    stats: [
      { value: "Por centro", label: "horas, aunque el personal cubra en otro sitio" },
      { value: "En directo", label: "vista de la sala en todos los centros" },
      { value: "Auto", label: "descansos y horas extra, sin cálculos a mano" },
    ],
    quote: {
      text: "Dos estudios, horarios largos y muchas coberturas. Ahora las horas caen en el centro correcto y la nómina es un solo archivo.",
      who: "Gerente, estudio de fitness · 14 empleados",
      result: "El cuadrante y la nómina por fin coinciden",
    },
    relatedSlugs: ["beauty", "healthcare", "hospitality"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "hospitality",
    name: "Hostelería",
    headline: "Control horario para cafeterías, restaurantes y hoteles: turnos, descansos y horas extra, resueltos con justicia",
    intro: "Turnos partidos, cierres tarde y un sábado con lleno que se alarga. Done-it registra las horas tal y como se trabajan, descuenta los descansos según tu regla y entrega a nóminas un solo archivo limpio.",
    pains: [
      {
        title: "El turno nunca acaba cuando dice el cuadrante",
        body: "El servicio se alarga y el cierre lleva media hora más. Esos minutos son trabajo real, y ajustarlos de memoria es como empiezan los conflictos.",
      },
      {
        title: "Los turnos partidos son difíciles de registrar",
        body: "Servicio de mediodía, descanso y luego cenas. En papel acaba siendo un bloque largo o dos estimaciones: ninguna es la verdad.",
      },
      {
        title: "Mucha rotación, formación constante",
        body: "Personal nuevo cada temporada. Cualquier sistema que exija formación no sobrevive a la primera semana fuerte.",
      },
    ],
    features: [
      {
        title: "Fichaje de entrada y salida por servicio",
        body: "Un toque al empezar y al terminar cada bloque, para que los turnos partidos queden registrados como fueron, no unidos en uno solo.",
      },
      {
        title: "Descansos según tu regla",
        body: "Descansos obligatorios descontados automáticamente, con excepciones por persona cuando el contrato lo exige. Sin cálculos a mano a fin de mes.",
      },
      {
        title: "Horas extra resueltas en la app",
        body: "La media hora de más tras el cierre se registra en cuanto se trabaja y se cobra o se compensa con tiempo libre: elige el empleado.",
      },
    ],
    compliance: {
      label: "Registro de turnos partidos",
      title: "Un registro defendible en un equipo con mucha rotación",
      body: "Done-it guarda un registro digital y objetivo de cada turno, descanso y hora extra: exactamente lo que pide ver una revisión de nóminas o una inspección de trabajo.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "El equipo ficha para el servicio", body: "Un toque al empezar el bloque y otro al terminar. Los turnos partidos siguen partidos." },
      { step: "Ves la sala en directo", body: "Quién está para el servicio de esta noche, quién sigue en su descanso, quién se quedó después del cierre." },
      { step: "Nóminas recibe las horas reales", body: "Tiempo trabajado, descansos y horas extra por empleado, exportados en un solo archivo." },
    ],
    stats: [
      { value: "Turnos partidos", label: "registrados como dos bloques, no como una estimación" },
      { value: "Auto", label: "descuento de descansos según tu regla" },
      { value: "1 min", label: "para dar de alta a alguien nuevo: es su propio móvil" },
    ],
    quote: {
      text: "El cierre siempre se alarga. Ahora esos minutos se registran en vez de discutirse, y el equipo puede recuperarlos como tiempo libre.",
      who: "Propietario, restaurante · 17 empleados",
      result: "Menos conflictos y nóminas más rápidas",
    },
    relatedSlugs: ["retail", "wellness", "beauty"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "healthcare",
    name: "Sanidad y cuidados",
    headline: "Control horario para equipos de cuidados: turnos, visitas a domicilio y titulaciones, en un solo registro",
    intro: "Clínicas, equipos de ayuda a domicilio y residencias funcionan con turnos, visitas y una documentación estricta. Done-it registra el tiempo trabajado por turno y por visita, y guarda los documentos de cada empleado donde puedes encontrarlos.",
    pains: [
      {
        title: "Visitas y turnos en sistemas distintos",
        body: "Las visitas a domicilio viven en una herramienta, las horas del cuadrante en otra y la nómina recibe una tercera versión. Cuadrarlas es una tarea mensual.",
      },
      {
        title: "Horas nocturnas y de fin de semana mal pagadas",
        body: "Las horas con complemento tienen tarifas distintas. Si el registro es aproximado, el pago también, y la corrección llega después.",
      },
      {
        title: "Los certificados caducan en silencio",
        body: "Las titulaciones y los certificados importan en el sector de cuidados, y están en una carpeta que nadie abre hasta que la pide una inspección.",
      },
    ],
    features: [
      {
        title: "Fichaje por turno o por visita",
        body: "Un toque al empezar el turno, o en cada domicilio en la atención a domicilio, con ubicación verificada donde necesitas la prueba.",
      },
      {
        title: "Horas desglosadas como las pagas",
        body: "Las horas nocturnas, de fin de semana y ordinarias se registran por separado, así que la exportación para nóminas ya refleja cómo se paga el trabajo.",
      },
      {
        title: "Documentos del empleado en un solo sitio",
        body: "Contratos, certificados y DNI guardados junto a la persona a la que pertenecen: se encuentran en segundos cuando alguien los pide.",
      },
    ],
    compliance: {
      label: "Registro de turnos y visitas",
      title: "Registros que aguantan una inspección",
      body: "Done-it mantiene un registro digital y objetivo del tiempo de trabajo de cada empleado (horas, descansos y desglose de complementos) junto a la documentación que corresponde a cada persona.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Ficha el turno o la visita", body: "Un toque en el centro o en el domicilio del paciente, con ubicación verificada donde hace falta la prueba." },
      { step: "Cada hora cae donde corresponde", body: "Horas ordinarias, nocturnas y de fin de semana registradas por separado según ocurren." },
      { step: "Exporta para nóminas", body: "Un archivo limpio por periodo, ya desglosado como lo pagas." },
    ],
    stats: [
      { value: "Por visita", label: "o por turno, según trabaje tu equipo" },
      { value: "Desglose", label: "de horas nocturnas y de fin de semana, tal como se trabajan" },
      { value: "Archivados", label: "certificados guardados por empleado" },
    ],
    quote: {
      text: "Las visitas a domicilio y los turnos del cuadrante por fin están en el mismo registro. La nómina ha dejado de ser un ejercicio de cuadre.",
      who: "Coordinadora, equipo de ayuda a domicilio · 22 empleados",
      result: "Un solo registro en lugar de tres",
    },
    relatedSlugs: ["wellness", "offices", "cleaning"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },
];
